import React from "react";
import { Box, Typography } from "@mui/material";
import { Bar, Line } from "recharts";

// Trazos de las gráficas de avance (inicio, objetivo e inversión). En
// acumulado es una tendencia: líneas (lo conseguido continuo, la predicción
// punteada). En diario cada día es una cantidad, no una tendencia: barras. La
// predicción va siempre en gris, para que se lea como estimación y no como
// dato. Lo previsto va en discontinua, para ver qué días quedan por debajo.
//
// Las filas llevan, por serie, `d<key>` (conseguido), `f<key>` (predicción) y
// `p<key>` (previsto).

export interface ProgressSeries {
  key: string;
  name: string;
  color: string;
  hasPlan: boolean;
  hasForecast: boolean;
}

type Row = Record<string, unknown>;

/**
 * Los trazos de todas las series, como hijos directos de un ComposedChart.
 * Con `stacked`, en diario las barras de las series se apilan (cantidades que
 * se suman); sin él van una al lado de otra (CTR, % del objetivo).
 */
export const progressSeriesElements = ({
  series,
  daily,
  stacked,
  background,
  forecastColor,
}: {
  series: ProgressSeries[];
  daily: boolean;
  stacked: boolean;
  /** Color del fondo de la tarjeta, para el borde del punto activo. */
  background: string;
  /** Gris de la predicción. */
  forecastColor: string;
}): React.ReactElement[] => {
  const plan = series
    .filter((s) => s.hasPlan)
    .map((s) => (
      <Line
        key={`p${s.key}`}
        dataKey={`p${s.key}`}
        stroke={s.color}
        strokeWidth={2}
        strokeDasharray="6 6"
        strokeOpacity={0.55}
        dot={false}
        activeDot={false}
        isAnimationActive={false}
      />
    ));

  if (daily) {
    const stackOf = (s: ProgressSeries) => (stacked ? "day" : s.key);
    return [
      ...series.map((s) => (
        <Bar
          key={`d${s.key}`}
          dataKey={`d${s.key}`}
          name={s.name}
          fill={s.color}
          stackId={stackOf(s)}
          maxBarSize={22}
          radius={stacked ? 0 : [3, 3, 0, 0]}
          isAnimationActive={false}
        />
      )),
      // El día que enlaza con lo real ya es una barra conseguida.
      ...series
        .filter((s) => s.hasForecast)
        .map((s) => (
          <Bar
            key={`f${s.key}`}
            dataKey={(row: Row) =>
              row[`d${s.key}`] == null ? row[`f${s.key}`] : null
            }
            fill={forecastColor}
            fillOpacity={0.55}
            stackId={stackOf(s)}
            maxBarSize={22}
            radius={stacked ? 0 : [3, 3, 0, 0]}
            isAnimationActive={false}
          />
        )),
      ...plan,
    ];
  }

  return [
    ...plan,
    ...series
      .filter((s) => s.hasForecast)
      .map((s) => (
        <Line
          key={`f${s.key}`}
          dataKey={`f${s.key}`}
          stroke={forecastColor}
          strokeWidth={2}
          strokeDasharray="2 4"
          strokeLinecap="round"
          dot={false}
          activeDot={false}
          isAnimationActive={false}
        />
      )),
    ...series.map((s) => (
      <Line
        key={`d${s.key}`}
        dataKey={`d${s.key}`}
        name={s.name}
        stroke={s.color}
        strokeWidth={2.5}
        dot={false}
        activeDot={{ r: 5, strokeWidth: 2, stroke: background }}
        isAnimationActive={false}
      />
    )),
  ];
};

/**
 * Leyenda de un trazo: la línea (continua, punteada o discontinua) o, en
 * diario, el cuadrado de la barra (`faded`: la de la predicción, más clara).
 */
export const LegendStroke: React.FC<{
  label: string;
  color: string;
  dash?: string;
  bar?: "solid" | "faded";
}> = ({ label, color, dash, bar }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    {bar ? (
      <Box
        aria-hidden
        sx={{
          width: 10,
          height: 10,
          borderRadius: "2px",
          bgcolor: color,
          opacity: bar === "faded" ? 0.55 : 1,
        }}
      />
    ) : (
      <svg width="24" height="4" aria-hidden>
        <line
          x1="1"
          y1="2"
          x2="23"
          y2="2"
          stroke={color}
          strokeWidth={2.5}
          strokeLinecap={dash === "1 4" ? "round" : undefined}
          strokeDasharray={dash}
        />
      </svg>
    )}
    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
      {label}
    </Typography>
  </Box>
);
